import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Clock, 
  ChevronLeft,
  Plus,
  Trash2,
  Save,
  Calendar,
  AlertCircle
} from 'lucide-react';

const DoctorSchedule = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;

  const daysOfWeek = [
    { key: 'saturday', en: 'Saturday', ar: 'السبت' },
    { key: 'sunday', en: 'Sunday', ar: 'الأحد' },
    { key: 'monday', en: 'Monday', ar: 'الاثنين' },
    { key: 'tuesday', en: 'Tuesday', ar: 'الثلاثاء' },
    { key: 'wednesday', en: 'Wednesday', ar: 'الأربعاء' },
    { key: 'thursday', en: 'Thursday', ar: 'الخميس' },
    { key: 'friday', en: 'Friday', ar: 'الجمعة' },
  ];

  const timeSlots = [
    '08:00 AM', '08:30 AM', '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM',
    '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM',
    '05:00 PM', '05:30 PM', '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM',
    '08:00 PM', '08:30 PM', '09:00 PM',
  ];

  const [schedule, setSchedule] = useState({
    saturday: { enabled: true, slots: [{ start: '09:00 AM', end: '05:00 PM' }] },
    sunday: { enabled: true, slots: [{ start: '09:00 AM', end: '05:00 PM' }] },
    monday: { enabled: true, slots: [{ start: '09:00 AM', end: '01:00 PM' }, { start: '04:00 PM', end: '08:00 PM' }] },
    tuesday: { enabled: true, slots: [{ start: '09:00 AM', end: '05:00 PM' }] },
    wednesday: { enabled: true, slots: [{ start: '09:00 AM', end: '05:00 PM' }] },
    thursday: { enabled: true, slots: [{ start: '09:00 AM', end: '01:00 PM' }] },
    friday: { enabled: false, slots: [] },
  });

  const [slotDuration, setSlotDuration] = useState('30');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const toggleDay = (day) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled,
        slots: !prev[day].enabled ? [{ start: '09:00 AM', end: '05:00 PM' }] : [],
      }
    }));
  };

  const updateSlot = (day, index, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: prev[day].slots.map((slot, i) => 
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const addSlot = (day) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: [...prev[day].slots, { start: '09:00 AM', end: '05:00 PM' }]
      }
    }));
  };

  const removeSlot = (day, index) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: prev[day].slots.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success(lang === 'ar' ? 'تم حفظ الجدول بنجاح' : 'Schedule saved successfully');
    setSaving(false);
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-schedule">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'إدارة الجدول' : 'Schedule Management'}
                </h1>
                <p className="text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'حدد أوقات توفرك للمواعيد' : 'Set your availability for appointments'}
                </p>
              </div>
            </div>
            <Button 
              className="bg-teal-600 hover:bg-teal-700" 
              onClick={handleSave}
              disabled={saving}
              data-testid="save-schedule-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? (lang === 'ar' ? 'جارٍ الحفظ...' : 'Saving...') : (lang === 'ar' ? 'حفظ' : 'Save')}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-3xl space-y-6">
        {/* Slot Duration */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {lang === 'ar' ? 'مدة الموعد' : 'Appointment Duration'}
            </CardTitle>
            <CardDescription>
              {lang === 'ar' ? 'حدد المدة الافتراضية لكل موعد' : 'Set the default duration for each appointment'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={slotDuration} onValueChange={setSlotDuration}>
              <SelectTrigger className="w-48" data-testid="slot-duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 {lang === 'ar' ? 'دقيقة' : 'minutes'}</SelectItem>
                <SelectItem value="30">30 {lang === 'ar' ? 'دقيقة' : 'minutes'}</SelectItem>
                <SelectItem value="45">45 {lang === 'ar' ? 'دقيقة' : 'minutes'}</SelectItem>
                <SelectItem value="60">60 {lang === 'ar' ? 'دقيقة' : 'minutes'}</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Weekly Schedule */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {lang === 'ar' ? 'الجدول الأسبوعي' : 'Weekly Schedule'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {daysOfWeek.map((day, dayIndex) => (
              <div key={day.key}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={schedule[day.key].enabled}
                      onCheckedChange={() => toggleDay(day.key)}
                      data-testid={`toggle-${day.key}`}
                    />
                    <Label className={`font-medium ${!schedule[day.key].enabled ? 'text-slate-400' : ''}`}>
                      {lang === 'ar' ? day.ar : day.en}
                    </Label>
                    {!schedule[day.key].enabled && (
                      <Badge variant="secondary" className="text-xs">
                        {lang === 'ar' ? 'مغلق' : 'Closed'}
                      </Badge>
                    )}
                  </div>
                  {schedule[day.key].enabled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => addSlot(day.key)}
                      className="text-teal-600"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {lang === 'ar' ? 'إضافة فترة' : 'Add slot'}
                    </Button>
                  )}
                </div>

                {schedule[day.key].enabled && (
                  <div className="space-y-2 ml-10">
                    {schedule[day.key].slots.map((slot, slotIndex) => (
                      <div key={slotIndex} className="flex items-center gap-2">
                        <Select
                          value={slot.start}
                          onValueChange={(value) => updateSlot(day.key, slotIndex, 'start', value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map(time => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-slate-400">{lang === 'ar' ? 'إلى' : 'to'}</span>
                        <Select
                          value={slot.end}
                          onValueChange={(value) => updateSlot(day.key, slotIndex, 'end', value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map(time => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {schedule[day.key].slots.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeSlot(day.key, slotIndex)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {dayIndex < daysOfWeek.length - 1 && <Separator className="mt-4" />}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">
                  {lang === 'ar' ? 'ملاحظة' : 'Note'}
                </p>
                <p className="text-sm text-blue-700">
                  {lang === 'ar' 
                    ? 'التغييرات في الجدول ستؤثر فقط على المواعيد الجديدة. المواعيد المحجوزة مسبقاً لن تتأثر.'
                    : 'Schedule changes will only affect new appointments. Previously booked appointments will not be affected.'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorSchedule;
